import React, { Component } from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';

import { scenePropTypes } from './Scenes/prop-types';

import SceneTab from './SceneTab';

/**
 * Scene
 */

class Scene extends Component {
  constructor(props) {
    super(props);

    /** Parameters */
    this.index = this.props.index;
    this.params = this.props.params;
    this.console = this.props.console;

    /** Set up the Scene timeline */
    this.timeline = gsap.timeline({
      defaults: this.params.timelineDefaults,
      onUpdateParams: [this.index],
      onUpdate: (index) => {
        /** Dispatch scroll up event to the App */
        if (index > 0) {
          const progress = this.timeline.progress();
          if (ScrollTrigger.getById(this.params.scrollTriggerId).direction === -1 && progress > 0) {
            this.props.onScrollUp(index);
          }
        }
      },
    });

    /** Refs */
    this.element = React.createRef();
    this.tab = React.createRef();
    this.content = React.createRef();
  }

  /**
   * Mount the slides tweens to the Scene timeline
   *
   * @param  {React.RefObject[]} slides - An array of slides RefObject objects
   * @param  {HTMLElement} tween - The scene tweenable element
   */
  mountSlides(slides, tween) {
    slides.forEach((slide, index) => {
      if (index > 0) {
        this.timeline
          .to(tween, {
            x: () => -slide.current.offsetWidth * index,
            onUpdateParams: [index, slides.length],
            onUpdate: (index, slidesCount) => this.dispatchSlideEvents(index, slidesCount),
          })
          .addLabel(`scene-${this.index}-slide-${index}`);
      }
    });
  }

  /**
   * Invoked once when the scene timeline starts
   *
   * @param {number} direction
   */
  onStart(direction) {
    // this.console(`onStart S${this.index}`, 'direction', direction);
    /** Start the first / last slide animations, regarding scroll direction */
    const slideIndex = direction === 1 ? 0 : this.content.current.slides.length - 1;
    this.startAnimation(slideIndex);
  }

  /**
   * Invoked once when the scene timeline ends
   *
   * @param {number} direction
   */
  onEnd(direction) {
    // this.console(`onEnd S${this.index}`, 'direction', direction);
    /** Stop the first / last slide animations, regarding scroll direction */
    const slideIndex = direction === 1 ? this.content.current.slides.length - 1 : 0;
    this.stopAnimation(slideIndex);
  }

  /**
   * Invoked multiple times when the scene is entering the screen
   *
   * @param {number} direction (1:up)|(-1:down) - The scroll direction
   * @param {number} progressLength - The main Timeline progress length
   *
   * @fires  Scene#onSlideEnter - Multiple times when the scene is entering the screen
   * @fires  SceneComponent#onSlideEnter - Multiple times when the scene is entering the screen
   */
  onEnter(direction, progressLength) {
    // this.console(`onEnter S${this.index}`, 'direction', direction, 'progressLength', progressLength);
    /**
     * Fires onSlideEnter for the first / last slide regarding the direction
     */
    const scenesAnchors = [...this.params.scenesAnchors, this.params.timelineEnd];
    const thresold = this.params.slideLength * this.params.slideEventsThresold;
    const slideIndex = direction === 1 ? 0 : this.content.current.slides.length - 1;
    if (
      (direction === 1 && progressLength >= scenesAnchors[this.index] - thresold) ||
      (direction === -1 && progressLength <= scenesAnchors[this.index + 1] - thresold)
    ) {
      this.onSlideEnter(direction, slideIndex);
      this.content.current.onSlideEnter && this.content.current.onSlideEnter(direction, slideIndex);
    }
  }

  /**
   * Invoked multiple times when the scene is leaving the screen
   *
   * @param {number} direction (1:up)|(-1:down) - The scroll direction
   * @param {number} progressLength - The main Timeline progress length
   *
   * @fires  Scene#onSlideLeave - Multiple times when the scene is leaving the screen
   * @fires  SceneComponent#onSlideLeave - Multiple times when the scene is leaving the screen
   */
  onLeave(direction, progressLength) {
    // this.console(`onLeave S${this.index}`, 'direction', direction, 'progressLength', progressLength);
    /**
     * Fires onSlideLeave for the first / last slide regarding the direction
     */
    const scenesAnchors = [...this.params.scenesAnchors, this.params.timelineEnd];
    const thresold = this.params.slideLength * this.params.slideEventsThresold;
    const slideIndex = direction === 1 ? this.content.current.slides.length - 1 : 0;
    if (
      (direction === 1 && progressLength >= scenesAnchors[this.index + 1] - thresold) ||
      (direction === -1 && progressLength <= scenesAnchors[this.index] - thresold)
    ) {
      this.onSlideLeave(direction, slideIndex);
      this.content.current.onSlideLeave && this.content.current.onSlideLeave(direction, slideIndex);
    }
  }

  /**
   * Invoked multiple times when a slide is entering the screen
   *
   * @param {number} direction (1:up)|(-1:down) - The scroll direction
   * @param {number} index - The index of the slide
   */
  onSlideEnter(direction, index) {
    // this.console('onSlideEnter slide', index);
    this.startSlideTweens(index);
  }

  /**
   * Invoked multiple times when a slide is leaving the screen
   *
   * @param {number} direction (1:up)|(-1:down) - The scroll direction
   * @param {number} index - The index of the slide
   */
  onSlideLeave(direction, index) {
    // this.console('onSlideLeave slide', index);
    this.stopSlideMedias(index);
  }

  /**
   * Start the scene animations
   */
  startAnimation() {
    // this.console(`startAnimations S${this.index}`);
    const target = this.index === 0 ? this.content.current.animation.current : this.content.current.heading.current.animation.current;
    target.play();
  }

  /**
   * Stop the scene animations
   */
  stopAnimation() {
    // this.console(`stopAnimations S${this.index}`);
    const target = this.index === 0 ? this.content.current.animation.current : this.content.current.heading.current.animation.current;
    target.pause();
  }

  /**
   * Start all the the tweens of a given slide
   *
   * @param  {number} index - The index of the slide
   * @param  {bool} immediate - Defines if the tweens should start immediately or with scroll velocity threshold
   */
  startSlideTweens(index, immediate = false) {
    const velocity = Math.abs(ScrollTrigger.getById(this.params.scrollTriggerId).getVelocity() / this.params.slideLength);
    /** Start the tweens only if the scroll velocity isn't too high */
    if ((immediate || velocity <= this.params.scrollVelocityThresold) && this.content.current.slidesTweens) {
      // this.console(`startSlideTweens S${this.index}.${index}`, 'velocity', velocity);
      const tweens = this.content.current.slidesTweens[index];
      Object.values(tweens).forEach((tween) => {
        const target = tween.current || tween;
        if (target.paused()) {
          target.play();
        }
      });
    }
  }

  /**
   * Stop all the medias of a given slide
   *
   * @param {number} index - The index of the slide
   */
  stopSlideMedias(index) {
    if (this.content.current.medias && this.content.current.medias[index]) {
      // this.console(`stopSlideMedias S${this.index}.${index}`);
      const media = this.content.current.medias[index].current;
      if (!media.paused()) {
        media.pause();
      }
    }
  }

  /**
   * Return the thresold applied to the timeline snap value for a slide
   *
   * @param  {number} index - The index of the slide
   * @param  {number} length - The number of slides
   * @param  {float} thresold - The thresold percentage
   * @return {float} - The thresold applied to the timeline snap value
   */
  getSnapWithThresold(index, length, thresold) {
    const snap = 1 / (length - 1);
    return snap * index + snap * thresold;
  }

  /**
   * Dispatch the Slide events
   *
   * @param  {number} index - The index of the slide
   * @param  {number} slidesCount - The number of slides
   *
   * @fires  Scene#onSlideEnter - Multiple times when the slide is entering the screen
   * @fires  Scene#onSlideLeave - Multiple times when the slide is leaving the screen
   * @fires  SceneComponent#onSlideEnter - Multiple times when the slide is entering the screen
   * @fires  SceneComponent#onSlideLeave - Multiple times when the slide is leaving the screen
   */
  dispatchSlideEvents(index, slidesCount) {
    const progress = this.timeline.progress();
    if (!this.props.isScrolling && progress > 0) {
      // this.console(`dispatchSlideEvents S${this.index}.${index}`, 'progress', progress);
      const direction = ScrollTrigger.getById(this.params.scrollTriggerId).direction;
      if (direction === 1) {
        if (progress >= this.getSnapWithThresold(index, slidesCount, -this.params.slideEventsThresold)) {
          /**
           * @fires  Scene#onSlideEnter - For the current slide, scrolling down
           * @fires  SceneComponent#onSlideEnter - For the current slide, scrolling down
           */
          this.onSlideEnter(direction, index);
          this.content.current.onSlideEnter && this.content.current.onSlideEnter(direction, index);
          if (index > 0) {
            /**
             * @fires  Scene#onSlideLeave - For the previous slide, scrolling down
             * @fires  SceneComponent#onSlideLeave - For the previous slide, scrolling down
             */
            this.onSlideLeave(direction, index - 1);
            this.content.current.onSlideLeave && this.content.current.onSlideLeave(direction, index - 1);
          }
        }
      } else {
        if (progress <= this.getSnapWithThresold(index - 1, slidesCount, this.params.slideEventsThresold)) {
          if (index > 0) {
            /**
             * @fires  Scene#onSlideEnter - For the previous slide, scrolling up
             * @fires  SceneComponent#onSlideEnter - For the previous slide, scrolling up
             */
            this.onSlideEnter(direction, index - 1);
            this.content.current.onSlideEnter && this.content.current.onSlideEnter(direction, index - 1);
          }
          /**
           * @fires  Scene#onSlideLeave - For the current slide, scrolling up
           * @fires  SceneComponent#onSlideLeave - For the current slide, scrolling up
           */
          this.onSlideLeave(direction, index);
          this.content.current.onSlideLeave && this.content.current.onSlideLeave(direction, index);
        }
      }
    }
  }

  render() {
    const SceneTag = this.index === 0 ? 'header' : 'section';
    const SceneComponent = this.props.Scene;
    return (
      <SceneTag ref={this.element} id={`scene${this.index}`} className={`scene ${SceneTag} scene${this.index}`} style={{ '--scene-index': this.index + 1 }}>
        <SceneTab
          ref={this.tab}
          index={this.index}
          title={this.props.data.title}
          downloadLink={this.props.downloadLink}
          onSceneChange={(event, index) => this.props.onSceneChange(event, index)}
        />
        <SceneComponent
          ref={this.content}
          index={this.index}
          data={this.props.data}
          params={this.params}
          title={this.props.data?.pageTitle || this.props.data.title}
          downloadLink={this.props.downloadLink}
          mountSlides={this.mountSlides.bind(this)}
          onSceneChange={(event, index) => this.props.onSceneChange(event, index)}
        />
      </SceneTag>
    );
  }
}

Scene.propTypes = scenePropTypes;

export default Scene;
